import React, { useState, useEffect, useCallback } from 'react';
import { votesApi } from '../../api';
import HrnbModal from '../controls/HrnbModal';
import HrnbButton from '../controls/HrnbButton';
import HrnbLoader from '../controls/HrnbLoader';
import HrnbAlert from '../controls/HrnbAlert';
import HrnbSlider from '../controls/HrnbSlider';
import HrnbTextArea from '../controls/HrnbTextArea';

/**
 * Модальное окно выставления оценки треку (жюри/зрители).
 * Использует Vote API: POST/PUT/DELETE /battles/{battleId}/rounds/{roundId}/vote
 *
 * Форма строится по правилу голосования раунда (GET .../rule):
 * - CRITERIA_BASED — слайдеры по критериям (criteriaScores), общий балл = сумма
 * - PLUS_MINUS_ZERO — выбор +/0/-
 * - NUMERIC_10 / CUSTOM_XYZ — числовой слайдер (для CUSTOM_XYZ валидация по validScores)
 *
 * @component
 * @param {boolean} props.open - Открыто ли окно
 * @param {Function} props.onClose - Обработчик закрытия
 * @param {string} props.battleId - UUID баттла
 * @param {string} props.roundId - UUID раунда
 * @param {Object} props.track - Трек (WorkTrackResponse.track + mediaUrl)
 * @param {Function} props.onVoted - Callback после успешного голосования
 */
export default function TrackVoteModal({ open, onClose, battleId, roundId, track, onVoted }) {
  const [session, setSession] = useState(null);
  const [rule, setRule] = useState(null);
  const [hasVoted, setHasVoted] = useState(false);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Нормализация определения критерия.
   * Сервер может отдавать разные варианты полей:
   * name/criterionName, minScore/min/minValue, maxScore/max/maxValue
   */
  const normalizeCriterion = useCallback((def, index, rule) => {
    const min = def.minScore ?? def.min ?? def.minValue ?? 0;
    const max = def.maxScore ?? def.max ?? def.maxValue ?? rule?.maxScore ?? 10;
    return {
      name: def.name ?? def.criterionName ?? `Критерий ${index + 1}`,
      minScore: Number.isFinite(+min) ? +min : 0,
      maxScore: Number.isFinite(+max) ? +max : 10
    };
  }, []);

  /** Инициализация формы по правилу голосования */
  const buildInitialForm = useCallback((votingRule) => {
    const initial = {
      score: votingRule?.minScore ?? 5,
      comment: '',
      criteriaScores: []
    };

    if (votingRule?.votingSystem === 'CRITERIA_BASED' && Array.isArray(votingRule.criteriaDefinitions)) {
      initial.criteriaScores = votingRule.criteriaDefinitions.map((def, idx) => {
        const c = normalizeCriterion(def, idx, votingRule);
        // Среднее значение диапазона как стартовая оценка
        return {
          criterionName: c.name,
          score: Math.floor((c.minScore + c.maxScore) / 2)
        };
      });
      const total = initial.criteriaScores.reduce((sum, cs) => sum + (Number.isFinite(cs.score) ? cs.score : 0), 0);
      if (initial.criteriaScores.length > 0) {
        initial.score = total;
      }
    }

    // Для PLUS_MINUS_ZERO стартуем с нейтрального «Ноль»
    if (votingRule?.votingSystem === 'PLUS_MINUS_ZERO') {
      initial.score = 0;
    }

    if (votingRule?.votingSystem === 'CUSTOM_XYZ' && Array.isArray(votingRule.validScores) && votingRule.validScores.length > 0) {
      initial.score = votingRule.validScores[Math.floor(votingRule.validScores.length / 2)];
    }

    // Защита от NaN
    if (!Number.isFinite(initial.score)) {
      initial.score = votingRule?.minScore ?? 5;
    }
    return initial;
  }, [normalizeCriterion]);

  // Загрузка данных при открытии окна
  useEffect(() => {
    if (!open || !battleId || !roundId || !track?.id) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      votesApi.getSession(battleId, roundId).catch(() => null),
      votesApi.getRule(battleId, roundId).catch(() => null),
      votesApi.hasVoted(battleId, roundId, track.id).catch(() => null)
    ]).then(([sessionData, ruleData, votedData]) => {
      if (cancelled) return;
      // Диагностика для бэкенда: точный URL и ответ проверки /voted
      console.log('[TrackVoteModal] Voted check URL:',
        `GET /battles/${battleId}/rounds/${roundId}/voted?trackId=${track.id}`,
        'trackId source: work.track.id =', track.id);
      console.log('[TrackVoteModal] Voted check response:', JSON.stringify(votedData));
      console.log('[TrackVoteModal] Rule:', JSON.stringify(ruleData));
      console.log('[TrackVoteModal] Session:', JSON.stringify(sessionData));
      setSession(sessionData);
      setRule(ruleData);
      setHasVoted(!!votedData?.hasVoted);
      setForm(buildInitialForm(ruleData));
      setLoading(false);
    });

    return () => { cancelled = true; };
  }, [open, battleId, roundId, track?.id, buildInitialForm]);

  const isSessionOpen = session?.status === 'OPEN';

  // Нормализованная система голосования (защита от регистра)
  const votingSystem = (rule?.votingSystem || '').toUpperCase();

  const VOTING_SYSTEM_LABELS = {
    PLUS_MINUS_ZERO: 'Плюс / Ноль / Минус',
    NUMERIC_10: 'Числовая шкала (0–10)',
    CRITERIA_BASED: 'По критериям',
    CUSTOM_XYZ: 'Кастомная шкала'
  };

  // Нормализованные критерии правила (или пусто, если их нет/некорректны)
  const usableCriteria = React.useMemo(() => {
    if (rule?.votingSystem !== 'CRITERIA_BASED' || !Array.isArray(rule?.criteriaDefinitions)) {
      return [];
    }
    return rule.criteriaDefinitions.map((def, idx) => normalizeCriterion(def, idx, rule));
  }, [rule, normalizeCriterion]);

  /** Изменить оценку по критерию (пересчитать общий балл) */
  const handleCriteriaChange = (criterionName, score) => {
    setForm(prev => {
      const criteriaScores = prev.criteriaScores || [];
      const existing = criteriaScores.findIndex(cs => cs.criterionName === criterionName);
      const newCriteriaScores = existing >= 0
        ? criteriaScores.map((cs, idx) => (idx === existing ? { ...cs, score } : cs))
        : [...criteriaScores, { criterionName, score }];
      return {
        ...prev,
        criteriaScores: newCriteriaScores,
        score: newCriteriaScores.reduce((sum, cs) => sum + (cs.score || 0), 0)
      };
    });
  };

  /** Собрать payload по правилу голосования */
  const buildPayload = () => {
    const safeScore = Number.isFinite(form.score) ? form.score : 0;
    const payload = {
      trackId: track.id,
      score: safeScore,
      comment: form.comment?.trim() || null
    };
    if (rule?.votingSystem === 'CRITERIA_BASED' && form.criteriaScores?.length > 0) {
      payload.criteriaScores = form.criteriaScores;
    }
    if (rule?.votingSystem === 'PLUS_MINUS_ZERO') {
      // form.score уже хранит -1 | 0 | 1 — нормализуем на всякий случай
      payload.score = safeScore > 0 ? 1 : safeScore < 0 ? -1 : 0;
    }
    // Диагностика: что уходит на сервер
    console.log('[TrackVoteModal] Vote payload:', JSON.stringify(payload));
    return payload;
  };

  const handleSubmit = async () => {
    if (rule?.votingSystem === 'CUSTOM_XYZ' && Array.isArray(rule.validScores) &&
        !rule.validScores.includes(form.score)) {
      setError(`Неверная оценка. Допустимые значения: ${rule.validScores.join(', ')}`);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const payload = buildPayload();
      console.log('[TrackVoteModal] Submitting vote:', hasVoted ? 'PUT' : 'POST',
        'battle:', battleId, 'round:', roundId, 'track:', track.id);
      let response;
      if (hasVoted) {
        try {
          response = await votesApi.updateVote(battleId, roundId, payload);
        } catch (updateErr) {
          // GET /voted мог ошибочно вернуть hasVoted: true (голоса на самом деле нет).
          // PUT не удался — пробуем создать голос через POST.
          console.warn('[TrackVoteModal] PUT failed, falling back to POST:',
            updateErr.response?.status, JSON.stringify(updateErr.response?.data));
          response = await votesApi.vote(battleId, roundId, payload);
          setHasVoted(true);
        }
      } else {
        response = await votesApi.vote(battleId, roundId, payload);
        setHasVoted(true);
      }
      // Диагностика: ответ сервера на голосование
      console.log('[TrackVoteModal] Vote response:', JSON.stringify(response));

      // Проверка: зафиксировал ли сервер голос (results агрегирует голоса отдельно от works)
      try {
        const results = await votesApi.getResults(battleId, roundId);
        console.log('[TrackVoteModal] Round results after vote:', JSON.stringify(results));
      } catch (resultsErr) {
        console.error('[TrackVoteModal] Failed to fetch results:', resultsErr.message);
      }

      onVoted?.(track.id, payload.score);
      onClose?.();
    } catch (err) {
      // Диагностика: полный ответ сервера при ошибке
      console.error('[TrackVoteModal] Vote failed:',
        err.response?.status, JSON.stringify(err.response?.data));
      setError(err.response?.data?.message ||
        `Не удалось отправить оценку${err.response?.status ? ` (ошибка ${err.response.status})` : ''}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Удалить свою оценку?')) return;
    setDeleting(true);
    setError(null);
    try {
      await votesApi.deleteVote(battleId, roundId, track.id);
      setHasVoted(false);
      onVoted?.(track.id, null);
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.message || 'Не удалось удалить оценку');
    } finally {
      setDeleting(false);
    }
  };

  // Для PLUS_MINUS_ZERO form.score хранит значение напрямую: -1 | 0 | 1
  const plusMinusLabel = (score) =>
    score < 0 ? '➖ Минус' : score > 0 ? '➕ Плюс' : '⭕ Ноль';

  return (
    <HrnbModal
      open={open}
      onClose={onClose}
      title={`Оценка трека: ${track?.title || 'Без названия'}`}
      size="medium"
    >
      <div className="track-vote-modal">
        {loading ? (
          <HrnbLoader text="Загрузка данных голосования..." />
        ) : !isSessionOpen ? (
          <HrnbAlert variant="info">
            {session?.status === 'CLOSED'
              ? '🔒 Сессия голосования закрыта — оценка недоступна'
              : '🗳️ Сессия голосования ещё не открыта'}
          </HrnbAlert>
        ) : form ? (
          <>
            {error && (
              <HrnbAlert variant="danger" onClose={() => setError(null)}>
                {error}
              </HrnbAlert>
            )}
            {hasVoted && (
              <HrnbAlert variant="success">
                ✅ Вы уже оценивали этот трек. Можно обновить или удалить оценку.
              </HrnbAlert>
            )}

            {/* Активная система голосования (из правила раунда) */}
            <div className="track-vote-system-badge">
              Система голосования:{' '}
              <strong>
                {VOTING_SYSTEM_LABELS[votingSystem] || votingSystem || 'не задана (шкала 0–10)'}
              </strong>
            </div>

            {/* Критериальная система */}
            {votingSystem === 'CRITERIA_BASED' && usableCriteria.length > 0 ? (
              <div className="track-vote-criteria">
                {usableCriteria.map((criteria, idx) => {
                  const criteriaScore = form.criteriaScores?.find(
                    cs => cs.criterionName === criteria.name
                  ) || { score: Math.floor((criteria.minScore + criteria.maxScore) / 2) };
                  return (
                    <div key={idx} className="track-vote-criteria-item">
                      <label className="track-vote-label">
                        {criteria.name}: <strong>{criteriaScore.score}</strong> / {criteria.maxScore}
                      </label>
                      <HrnbSlider
                        min={criteria.minScore}
                        max={criteria.maxScore}
                        step={1}
                        value={Number.isFinite(criteriaScore.score) ? criteriaScore.score : criteria.minScore}
                        onChange={(val) => handleCriteriaChange(criteria.name, val)}
                      />
                    </div>
                  );
                })}
                <div className="track-vote-total">
                  Общий балл: <strong>{form.score}</strong> / {rule.maxScore}
                </div>
              </div>
            ) : votingSystem === 'PLUS_MINUS_ZERO' ? (
              /* +/0/- */
              <div className="track-vote-score">
                <label className="track-vote-label">
                  Ваша оценка: {plusMinusLabel(form.score)}
                </label>
                <div className="track-vote-plusminus">
                  {[['➖ Минус', -1], ['⭕ Ноль', 0], ['➕ Плюс', 1]].map(([label, value]) => (
                    <HrnbButton
                      key={value}
                      variant={form.score === value ? 'primary' : 'outline'}
                      size="sm"
                      onClick={() => setForm(prev => ({ ...prev, score: value }))}
                    >
                      {label}
                    </HrnbButton>
                  ))}
                </div>
              </div>
            ) : (
              /* Числовая шкала (NUMERIC_10 / CUSTOM_XYZ) */
              <div className="track-vote-score">
                <label className="track-vote-label">
                  Ваша оценка: <strong>{form.score}</strong> / {rule?.maxScore || 10}
                  {rule?.votingSystem === 'CUSTOM_XYZ' && Array.isArray(rule.validScores) && (
                    <span className="track-vote-valid-scores">
                      {' '}(допустимо: {rule.validScores.join(', ')})
                    </span>
                  )}
                </label>
                <HrnbSlider
                  min={rule?.minScore ?? 0}
                  max={rule?.maxScore || 10}
                  step={1}
                  value={Number.isFinite(form.score) ? form.score : (rule?.minScore ?? 5)}
                  onChange={(val) => setForm(prev => ({ ...prev, score: val }))}
                />
              </div>
            )}

            {/* Комментарий */}
            <div className="track-vote-comment">
              <label className="track-vote-label">Комментарий (опционально)</label>
              <HrnbTextArea
                value={form.comment || ''}
                onChange={(e) => setForm(prev => ({ ...prev, comment: e.target.value }))}
                placeholder="Ваш комментарий к треку..."
                rows={3}
              />
            </div>

            {/* Действия */}
            <div className="track-vote-actions">
              <HrnbButton
                variant="primary"
                onClick={handleSubmit}
                disabled={submitting || deleting}
              >
                {submitting ? <HrnbLoader size="sm" /> : hasVoted ? 'Обновить оценку' : 'Отправить оценку'}
              </HrnbButton>
              {hasVoted && (
                <HrnbButton
                  variant="outline-danger"
                  onClick={handleDelete}
                  disabled={submitting || deleting}
                >
                  {deleting ? <HrnbLoader size="sm" /> : 'Удалить оценку'}
                </HrnbButton>
              )}
              <HrnbButton variant="outline" onClick={onClose} disabled={submitting || deleting}>
                Отмена
              </HrnbButton>
            </div>
          </>
        ) : null}
      </div>
    </HrnbModal>
  );
}
